import { call, put, takeLatest, all } from 'redux-saga/effects';

import { BOOST } from '../../../components/global/Server/endpoints';
import { HTTP_SUCCESS } from '../../constants/global';
import * as T from '../../constants/boost';
import {
  fetchBoostHistory,
  fetchBoostDetail,
  storeBoostHistory,
  storeBoostDetail,
  storeBoostSending,
  storeBoostSendResult,
  storeBoostCustomers,
  storeBoostTemplates,
  storeBoostCredit,
  storeBoostSettings,
  storeBoostAdmin,
  storeBoostAnalytics,
  storeBoostPlatformConfig,
  fetchBoostCredit,
} from '../../actions/boost';
import {
  clearStatus,
  requestStatus,
  showErrorMessage,
} from '../../actions/global';
import { storeResponseMessage } from '../../actions/settings';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import AuthUtil from '../../AuthUtil';

// Generic POST helper that updates global status + handles errors uniformly.
function* postRequest(url, data) {
  const response = yield call(() => AuthUtil.postRequest(url, data));
  const success =
    response.status === HTTP_SUCCESS && response.data && response.data.success;
  yield put(
    requestStatus({
      status: response.status,
      statusText: response.data?.message || response.statusText,
      success,
    })
  );
  return { response, success };
}

// ---- Send ------------------------------------------------------------------

function* sendBoostMessageSaga(action) {
  yield call(clearStatus);
  yield put(storeBoostSendResult(null));
  yield put(storeBoostSending(true));
  let outcome = { ok: false, message: SERVER_FAILED_ERROR_MESSAGE };
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.SEND,
      action.data
    );
    if (success) {
      outcome = { ok: true };
      yield put(storeResponseMessage('Message sent'));
      if (action.data?.locationId) {
        yield put(fetchBoostHistory({ locationId: action.data.locationId }));
        yield put(fetchBoostCredit({ locationId: action.data.locationId }));
      }
    } else {
      outcome = {
        ok: false,
        message: response.data?.message || SERVER_FAILED_ERROR_MESSAGE,
      };
      yield put(showErrorMessage(outcome.message));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    // ts makes every result a fresh object so the composer's effect
    // fires even when two consecutive sends have the same outcome.
    yield put(storeBoostSendResult({ ...outcome, ts: Date.now() }));
    yield put(storeBoostSending(false));
  }
}

function* sendBoostBulkSaga(action) {
  yield call(clearStatus);
  yield put(storeBoostSendResult(null));
  yield put(storeBoostSending(true));
  let outcome = { ok: false, message: SERVER_FAILED_ERROR_MESSAGE };
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.SEND_BULK,
      action.data
    );
    if (success) {
      outcome = { ok: true };
      yield put(
        storeResponseMessage(
          action.data?.sendMode === 'schedule'
            ? 'Message scheduled'
            : 'Message sent'
        )
      );
      if (action.data?.locationId) {
        yield put(fetchBoostHistory({ locationId: action.data.locationId }));
        yield put(fetchBoostCredit({ locationId: action.data.locationId }));
      }
    } else {
      outcome = {
        ok: false,
        message: response.data?.message || SERVER_FAILED_ERROR_MESSAGE,
      };
      yield put(showErrorMessage(outcome.message));
      // A failed send may have refunded credit server-side and created
      // a failed message row — refresh both so the dashboard shows the
      // corrected balance AND the message appears in the Failed bucket.
      if (action.data?.locationId) {
        yield put(fetchBoostCredit({ locationId: action.data.locationId }));
        yield put(fetchBoostHistory({ locationId: action.data.locationId }));
      }
    }
  } catch (e) {
    // The request errored/timed out on the wire. The backend now dispatches
    // asynchronously, so the send is most likely still running (or already
    // done) server-side — pull History + credit so the message shows up with
    // its real status instead of leaving the owner with a bare failure.
    outcome = {
      ok: false,
      message:
        'The send is taking longer than expected — check Message History in a moment for delivery status.',
    };
    yield put(showErrorMessage(outcome.message));
    if (action.data?.locationId) {
      yield put(fetchBoostHistory({ locationId: action.data.locationId }));
      yield put(fetchBoostCredit({ locationId: action.data.locationId }));
    }
  } finally {
    yield put(storeBoostSendResult({ ...outcome, ts: Date.now() }));
    yield put(storeBoostSending(false));
  }
}

function* retryBoostSaga(action) {
  yield call(clearStatus);
  yield put(storeBoostSending(true));
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.RETRY_FAILED,
      action.data
    );
    if (success) {
      yield put(storeResponseMessage('Message sent'));
    } else {
      yield put(
        showErrorMessage(response.data?.message || SERVER_FAILED_ERROR_MESSAGE)
      );
    }
    // Either way the message + ledger changed (attempt counts, refunds)
    // — refresh everything the detail screen shows.
    if (action.data?.locationId) {
      yield put(fetchBoostHistory({ locationId: action.data.locationId }));
      yield put(fetchBoostCredit({ locationId: action.data.locationId }));
    }
    if (action.data?.messageId) {
      yield put(fetchBoostDetail({ messageId: action.data.messageId }));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(storeBoostSending(false));
  }
}

// Per-recipient retry — one failed row in the Delivery Report. Refreshes
// history/credit (attempt count + ledger changed) and the open detail so
// the retried row moves out of Failed the moment it's accepted.
function* retryBoostRecipientSaga(action) {
  yield call(clearStatus);
  yield put(storeBoostSending(true));
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.RETRY_RECIPIENT,
      action.data
    );
    if (success) {
      yield put(storeResponseMessage(response.data?.message || 'Retry sent'));
    } else {
      yield put(
        showErrorMessage(response.data?.message || SERVER_FAILED_ERROR_MESSAGE)
      );
    }
    if (action.data?.locationId) {
      yield put(fetchBoostHistory({ locationId: action.data.locationId }));
      yield put(fetchBoostCredit({ locationId: action.data.locationId }));
    }
    if (action.data?.messageId) {
      yield put(fetchBoostDetail({ messageId: action.data.messageId }));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(storeBoostSending(false));
  }
}

function* cancelBoostSaga(action) {
  try {
    yield call(postRequest, BOOST.CANCEL, action.data);
    if (action.data?.locationId) {
      yield put(fetchBoostHistory({ locationId: action.data.locationId }));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// ---- History / detail ------------------------------------------------------

function* fetchBoostHistorySaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BOOST.LIST, action.data)
    );
    if (response.status === HTTP_SUCCESS && response.data?.success) {
      yield put(storeBoostHistory(response.data.messages || []));
    } else {
      yield put(storeBoostHistory([]));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* fetchBoostDetailSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BOOST.DETAIL, action.data)
    );
    if (response.status === HTTP_SUCCESS && response.data?.success) {
      yield put(
        storeBoostDetail({
          message: response.data.message,
          recipients: response.data.recipients || [],
          optOutRecipients: response.data.optOutRecipients || [],
        })
      );
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// ---- Audience / customers --------------------------------------------------

function* fetchBoostCustomersSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BOOST.AUDIENCE_CUSTOMERS, action.data)
    );
    if (response.status === HTTP_SUCCESS && response.data?.success) {
      yield put(storeBoostCustomers(response.data.customers || []));
    } else {
      yield put(storeBoostCustomers([]));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// ---- Templates -------------------------------------------------------------

function* fetchBoostTemplatesSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BOOST.TEMPLATE_LIST, action.data)
    );
    if (response.status === HTTP_SUCCESS && response.data?.success) {
      yield put(storeBoostTemplates(response.data.templates || []));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveBoostTemplateSaga(action) {
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.TEMPLATE_SAVE,
      action.data
    );
    if (success) {
      yield put(storeBoostTemplates(response.data.templates || []));
      yield put(storeResponseMessage('Template saved'));
    } else {
      yield put(
        showErrorMessage(response.data?.message || SERVER_FAILED_ERROR_MESSAGE)
      );
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteBoostTemplateSaga(action) {
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.TEMPLATE_DELETE,
      action.data
    );
    if (success) {
      yield put(storeBoostTemplates(response.data.templates || []));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// ---- Credit ----------------------------------------------------------------

function* fetchBoostCreditSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BOOST.CREDIT_STATUS, action.data)
    );
    if (response.status === HTTP_SUCCESS && response.data?.success) {
      yield put(storeBoostCredit(response.data.credit));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* rechargeBoostCreditSaga(action) {
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.CREDIT_RECHARGE,
      action.data
    );
    if (success) {
      yield put(storeBoostCredit(response.data.credit));
      yield put(storeResponseMessage('Credit added'));
    } else {
      yield put(
        showErrorMessage(response.data?.message || SERVER_FAILED_ERROR_MESSAGE)
      );
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// ---- Settings --------------------------------------------------------------

function* fetchBoostSettingsSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BOOST.SETTINGS_GET, action.data)
    );
    if (response.status === HTTP_SUCCESS && response.data?.success) {
      yield put(storeBoostSettings(response.data.settings));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveBoostSettingsSaga(action) {
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.SETTINGS_SAVE,
      action.data
    );
    if (success) {
      yield put(storeBoostSettings(response.data.settings));
      yield put(storeResponseMessage('Settings saved'));
    } else {
      yield put(
        showErrorMessage(response.data?.message || SERVER_FAILED_ERROR_MESSAGE)
      );
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// ---- Admin (platform owner) ------------------------------------------------

function* fetchBoostAdminSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BOOST.ADMIN_GET, action.data)
    );
    if (response.status === HTTP_SUCCESS && response.data?.success) {
      yield put(
        storeBoostAdmin({
          settings: response.data.settings,
          credit: response.data.credit,
        })
      );
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveBoostAdminSaga(action) {
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.ADMIN_SAVE,
      action.data
    );
    if (success) {
      yield put(
        storeBoostAdmin({
          settings: response.data.settings,
          credit: response.data.credit,
        })
      );
      yield put(storeResponseMessage('Admin settings saved'));
    } else {
      yield put(
        showErrorMessage(response.data?.message || SERVER_FAILED_ERROR_MESSAGE)
      );
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* resetBoostBalanceSaga(action) {
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.ADMIN_RESET,
      action.data
    );
    if (success) {
      yield put(storeBoostCredit(response.data.credit));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* pauseBoostSaga(action) {
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.ADMIN_PAUSE,
      action.data
    );
    if (success) {
      yield put(storeBoostCredit(response.data.credit));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// ---- Analytics -------------------------------------------------------------

function* fetchBoostAnalyticsSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BOOST.ANALYTICS, action.data)
    );
    if (response.status === HTTP_SUCCESS && response.data?.success) {
      yield put(
        storeBoostAnalytics({
          months: response.data.months || [],
          allTime: response.data.allTime || {},
          messages: response.data.messages || [],
        })
      );
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* fetchBoostPlatformConfigSaga() {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BOOST.PLATFORM_CONFIG_GET, {})
    );
    if (response.status === HTTP_SUCCESS && response.data?.success) {
      yield put(storeBoostPlatformConfig(response.data.config));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveBoostPlatformConfigSaga(action) {
  try {
    const { response, success } = yield call(
      postRequest,
      BOOST.PLATFORM_CONFIG_SAVE,
      action.data
    );
    if (success) {
      yield put(storeBoostPlatformConfig(response.data.config));
      yield put(storeResponseMessage('Platform config saved'));
    } else {
      yield put(
        showErrorMessage(response.data?.message || SERVER_FAILED_ERROR_MESSAGE)
      );
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// ---- Root saga -------------------------------------------------------------

export default function* boostSaga() {
  yield all([
    takeLatest(T.SEND_BOOST_MESSAGE, sendBoostMessageSaga),
    takeLatest(T.SEND_BOOST_BULK, sendBoostBulkSaga),
    takeLatest(T.RETRY_BOOST_MESSAGE, retryBoostSaga),
    takeLatest(T.RETRY_BOOST_RECIPIENT, retryBoostRecipientSaga),
    takeLatest(T.CANCEL_BOOST_MESSAGE, cancelBoostSaga),
    takeLatest(T.FETCH_BOOST_HISTORY, fetchBoostHistorySaga),
    takeLatest(T.FETCH_BOOST_DETAIL, fetchBoostDetailSaga),
    takeLatest(T.FETCH_BOOST_CUSTOMERS, fetchBoostCustomersSaga),
    takeLatest(T.FETCH_BOOST_TEMPLATES, fetchBoostTemplatesSaga),
    takeLatest(T.SAVE_BOOST_TEMPLATE, saveBoostTemplateSaga),
    takeLatest(T.DELETE_BOOST_TEMPLATE, deleteBoostTemplateSaga),
    takeLatest(T.FETCH_BOOST_CREDIT, fetchBoostCreditSaga),
    takeLatest(T.RECHARGE_BOOST_CREDIT, rechargeBoostCreditSaga),
    takeLatest(T.FETCH_BOOST_SETTINGS, fetchBoostSettingsSaga),
    takeLatest(T.SAVE_BOOST_SETTINGS, saveBoostSettingsSaga),
    takeLatest(T.FETCH_BOOST_ADMIN, fetchBoostAdminSaga),
    takeLatest(T.SAVE_BOOST_ADMIN, saveBoostAdminSaga),
    takeLatest(T.RESET_BOOST_BALANCE, resetBoostBalanceSaga),
    takeLatest(T.PAUSE_BOOST, pauseBoostSaga),
    takeLatest(T.FETCH_BOOST_ANALYTICS, fetchBoostAnalyticsSaga),
    takeLatest(T.FETCH_BOOST_PLATFORM_CONFIG, fetchBoostPlatformConfigSaga),
    takeLatest(T.SAVE_BOOST_PLATFORM_CONFIG, saveBoostPlatformConfigSaga),
  ]);
}
